import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import Login from './components/auth/Login';
import Dashboard from './pages/Dashboard';
import FinanceDashboard from './pages/finance/FinanceDashboard';
import TransactionForm from './pages/finance/TransactionForm';
import TransactionsList from './pages/finance/TransactionsList';
import TransactionEdit from './pages/finance/TransactionEdit';
import PlayersList from './pages/players/PlayersList';
import PlayerForm from './pages/players/PlayerForm';
import PlayerDetail from './pages/players/PlayerDetail';
import PlayerEdit from './pages/players/PlayerEdit';
import PlayerStats from './pages/players/PlayerStats';
import TrainingsList from './pages/trainings/TrainingsList';
import TrainingForm from './pages/trainings/TrainingForm';
import AttendanceControl from './pages/trainings/AttendanceControl';
import MatchesList from './pages/matches/MatchesList';
import MatchForm from './pages/matches/MatchForm';
import MatchCallup from './pages/matches/MatchCallup';
import MatchStats from './pages/matches/MatchStats';
import AlertsManagement from './pages/alerts/AlertsManagement';
import CoachDashboard from './pages/coach/CoachDashboard';
import CoachReports from './pages/coach/CoachReports';
import UsersList from './pages/users/UsersList';
import UserForm from './pages/users/UserForm';
import ProtectedRoute from './components/common/ProtectedRoute';

function App() {
  return (
    <Router>
      <AuthProvider>
        <Routes>
          {/* Ruta raíz redirige al dashboard */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          
          {/* Ruta de login (pública) */}
          <Route path="/login" element={<Login />} />
          
          {/* Ruta del dashboard principal (protegida) */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />

          {/* Rutas de Finanzas (protegidas - solo admin y tesorero) */}
          <Route
            path="/finance"
            element={
              <ProtectedRoute allowedRoles={['admin', 'tesorero']}>
                <FinanceDashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/finance/transactions"
            element={
              <ProtectedRoute allowedRoles={['admin', 'tesorero']}>
                <TransactionsList />
              </ProtectedRoute>
            }
          />

          <Route
            path="/finance/transactions/new"
            element={
              <ProtectedRoute allowedRoles={['admin', 'tesorero']}>
                <TransactionForm />
              </ProtectedRoute>
            }
          />

          <Route
            path="/finance/transactions/edit/:id"
            element={
              <ProtectedRoute allowedRoles={['admin', 'tesorero']}>
                <TransactionEdit />
              </ProtectedRoute>
            }
          />

          {/* Rutas de Jugadores (protegidas - admin y entrenadores) */}
          <Route
            path="/players"
            element={
              <ProtectedRoute allowedRoles={['admin', 'entrenador']}>
                <PlayersList />
              </ProtectedRoute>
            }
          />

          <Route
            path="/players/new"
            element={
              <ProtectedRoute allowedRoles={['admin', 'entrenador']}>
                <PlayerForm />
              </ProtectedRoute>
            }
          />

          <Route
            path="/players/:id"
            element={
              <ProtectedRoute allowedRoles={['admin', 'entrenador']}>
                <PlayerDetail />
              </ProtectedRoute>
            }
          />

          <Route
            path="/players/edit/:id"
            element={
              <ProtectedRoute allowedRoles={['admin', 'entrenador']}>
                <PlayerEdit />
              </ProtectedRoute>
            }
          />

          <Route
            path="/players/:id/stats"
            element={
              <ProtectedRoute allowedRoles={['admin', 'entrenador']}>
                <PlayerStats />
              </ProtectedRoute>
            }
          />

          {/* Rutas de Entrenamientos (protegidas - admin y entrenadores) */}
          <Route
            path="/trainings"
            element={
              <ProtectedRoute allowedRoles={['admin', 'entrenador']}>
                <TrainingsList />
              </ProtectedRoute>
            }
          />

          <Route
            path="/trainings/new"
            element={
              <ProtectedRoute allowedRoles={['admin', 'entrenador']}>
                <TrainingForm />
              </ProtectedRoute>
            }
          />

          <Route
            path="/trainings/:id/attendance"
            element={
              <ProtectedRoute allowedRoles={['admin', 'entrenador']}>
                <AttendanceControl />
              </ProtectedRoute>
            }
          />

          <Route
            path="/trainings/edit/:id"
            element={
              <ProtectedRoute allowedRoles={['admin', 'entrenador']}>
                <TrainingForm />
              </ProtectedRoute>
            }
          />

          {/* Rutas de Partidos (protegidas - admin y entrenadores) */}
          <Route
            path="/matches"
            element={
              <ProtectedRoute allowedRoles={['admin', 'entrenador']}>
                <MatchesList />
              </ProtectedRoute>
            }
          />

          <Route
            path="/matches/new"
            element={
              <ProtectedRoute allowedRoles={['admin', 'entrenador']}>
                <MatchForm />
              </ProtectedRoute>
            }
          />

          <Route
            path="/matches/:id/callup"
            element={
              <ProtectedRoute allowedRoles={['admin', 'entrenador']}>
                <MatchCallup />
              </ProtectedRoute>
            }
          />

          <Route
            path="/matches/:id/stats"
            element={
              <ProtectedRoute allowedRoles={['admin', 'entrenador']}>
                <MatchStats />
              </ProtectedRoute>
            }
          />

          <Route
            path="/matches/edit/:id"
            element={
              <ProtectedRoute allowedRoles={['admin', 'entrenador']}>
                <MatchForm />
              </ProtectedRoute>
            }
          />

          {/* Rutas de Alertas (protegidas - admin y entrenadores) */}
          <Route
            path="/alerts"
            element={
              <ProtectedRoute allowedRoles={['admin', 'entrenador']}>
                <AlertsManagement />
              </ProtectedRoute>
            }
          />

          {/* Rutas del Dashboard del Entrenador */}
          <Route
            path="/coach"
            element={
              <ProtectedRoute allowedRoles={['admin', 'entrenador']}>
                <CoachDashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/coach/reports"
            element={
              <ProtectedRoute allowedRoles={['admin', 'entrenador']}>
                <CoachReports />
              </ProtectedRoute>
            }
          />

          {/* Rutas de Usuarios (protegidas - solo admin) */}
          <Route
            path="/users"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <UsersList />
              </ProtectedRoute>
            }
          />

          <Route
            path="/users/new"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <UserForm />
              </ProtectedRoute>
            }
          />

          {/* Ruta por defecto para páginas no encontradas */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </AuthProvider>
    </Router>
  );
}

export default App;